import { FunctionComponent, useEffect, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import { useMutation } from '@apollo/client'
import styles from './staffSuggest.module.scss'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionsDeprecated } from '@/types/Select'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { SET_STAFF_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface StaffSuggestProps {
    daycare: Daycare
    staffDistance: string
    setStaffDistance: (value: string) => void
    canUpdate: boolean
}

const StaffSuggest: FunctionComponent<StaffSuggestProps> = ({
    daycare,
    staffDistance,
    setStaffDistance,
    canUpdate
}: StaffSuggestProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newStaffDistance, setNewStaffDistance] = useState<string>(staffDistance)

    useEffect(() => {
        setNewStaffDistance(staffDistance)
    }, [staffDistance])

    const staffDistanceOptions: SelectOptionsDeprecated = [
        {
            options: [
                {
                    name: ' 5 km',
                    value: 'distance_5'
                },
                {
                    name: ' 15 km',
                    value: 'distance_15'
                },
                {
                    name: '30 km',
                    value: 'distance_30'
                },
                {
                    name: '50 km',
                    value: 'distance_50'
                }
            ]
        }
    ]

    const [setStaffSuggestSettings] = useMutation(SET_STAFF_SUGGEST_SETTINGS, {
        variables: {
            daycareId: daycare.id,
            distance: newStaffDistance
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            setStaffDistance(newStaffDistance)

            showNotificationSuccess(t('suggestSettingsUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdatingSuggestSettings'))
        }
    })

    const disabled: boolean = staffDistance === newStaffDistance

    return (
        <div className={styles.StaffSuggestPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('staffSettings')} className={styles.Title} />
                </div>
                <Table className={styles.SettingsTable}>
                    <TableBody className={styles.TableBody}>
                        <TableRow className={styles.TableRow}>
                            <TableCell className={styles.RowText}>{t('inRadiusOf')}</TableCell>
                            <TableCell className={styles.HideBorder}>
                                <SelectUnique
                                    selectOptions={staffDistanceOptions}
                                    selectValue={newStaffDistance}
                                    setSelectValue={setNewStaffDistance}
                                    fullWidth={true}
                                    formClassName={styles.UserInput}
                                    readOnly={!canUpdate}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                {canUpdate && (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'update'}
                            onClick={setStaffSuggestSettings}
                            disabled={disabled}
                            className={styles.Button}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default StaffSuggest
